package ai.wagemix.data

import ai.wagemix.net.models.WMAuthFailure
import ai.wagemix.net.models.WMUploadFailureReason
import com.fsryan.foundation.net.client.FSResponse

/** The API (or a callable) answered with an error, or with no body where one was required. */
class WMRemoteException(val status: Int, message: String, cause: Throwable? = null): Exception(message, cause) {
    val isPaymentRequired: Boolean get() = status == 402
    val isUnauthorized: Boolean get() = status == 401 || status == 403
    val isNotFound: Boolean get() = status == 404
}

/** An authentication step the identity provider refused, with the typed reason the UI renders. */
class WMAuthException(val reason: WMAuthFailure, message: String?): Exception(message ?: reason.name)

/** An operation that needs a signed-in user ran without one. */
class WMUnauthenticatedException: Exception("No signed-in user")

/**
 * A job-description upload produced no mix for a known [reason] (not a job description, a posting that would not
 * open, malware, a failed analysis); [message] is the API's explanation for the user. Such uploads are free.
 */
class WMUploadNotAcceptedException(val reason: WMUploadFailureReason, message: String): Exception(message)

/** A shared mix whose owner turned its link off: Firestore refuses to read it (planning/share-link-stats-plan.md). */
class WMSharedLinkDeactivatedException: Exception("This link has been deactivated")

/**
 * Firestore refused a read or write of the user's paid data (saved mixes, uploads, their share list): the rules allow it
 * only on a Basic or Pro plan (planning/paid-feature-enforcement-plan.md W3). A lapsed account meets it until it renews.
 */
class WMPlanRequiredException: Exception("A Basic or Pro plan is needed")

/** The Basic plan's job-description quota is used up (HTTP 402 from the API). */
class WMQuotaExceededException(message: String?): Exception(message ?: "Upload quota exceeded")

/**
 * The body of a successful response, or a [WMRemoteException] whose message [describe] builds
 * from the transport's exception (which may be `null` when the server simply sent no body).
 */
fun <D: Any> FSResponse<D, *>.dataOrThrow(describe: (Throwable?) -> String): D {
    return data ?: throw WMRemoteException(status = status, message = describe(exception), cause = exception)
}

/** Throws a [WMRemoteException] built by [describe] unless the status is 2xx. */
fun FSResponse<*, *>.throwUnlessSuccess(describe: (Throwable?) -> String) {
    if (status !in 200..299) {
        throw WMRemoteException(status = status, message = describe(exception), cause = exception)
    }
}
